import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, EffectFade } from "swiper/modules"
import { motion, useScroll, useTransform } from "framer-motion"
import "swiper/css"
import "swiper/css/effect-fade"
import About from "../Components/About/About"
import Path from "../Components/Path/Path"
import Programes from "../Components/Programes/Programes"
import Blog from "../Components/Blog/Blog"
import Footer from "../Components/Outlets/Footer"
import Nav from "../Components/Outlets/Nav"
import hero1 from "../Assets/campus1.jpg"
import hero2 from "../Assets/campus2.jpg"
import hero3 from "../Assets/campus3.jpg"

const heroSlides = [hero1, hero2, hero3]

const faqs = [
	{
		q: "Why Branmont Global Secondary School?",
		a: "Branmont Global Secondary School is a residential campus that combines academic excellence with intensive exam preparation, character-building, and university pathways — all within a structured, supportive learning environment."
	},
	{
		q: "What entrance exams do you coach for?",
		a: "We offer integrated coaching for NEET, JEE, CA Foundation, CMA Foundation, and other national-level competitive exams, paired with strong +1 & +2 academic foundations."
	},
	{
		q: "Do you offer pathways to higher education and careers?",
		a: "Yes. Branmont provides guided university and professional pathways, scholarship counseling, and profile development to help students move confidently into future opportunities."
	},
	{
		q: "What kind of learners thrive here?",
		a: "Branmont is built for focused, disciplined, and purpose-driven learners who want a structured residential environment that supports both academic success and personal growth."
	}
]

const HomePage = () => {
	const [openFaq, setOpenFaq] = useState(0)
	const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? -1 : idx)

	const { scrollYProgress } = useScroll()
	const y = useTransform(scrollYProgress, [0, 1], [0, -200])

	return (
		<div>
			<Nav />
			<div className="page-main">
				<div className="hero-slider-wrap">
					<Swiper
						modules={[Autoplay, EffectFade]}
						effect="fade"
						autoplay={{ delay: 4500, disableOnInteraction: false }}
						loop={true}
						speed={1200}
						className="hero-swiper"
					>
						{heroSlides.map((img, i) => (
							<SwiperSlide key={i}>
								<motion.div 
									className="hero-slide" 
									style={{ backgroundImage: `url(${img})`, y: i === 0 ? y : 0 }} 
								/>
							</SwiperSlide>
						))}
					</Swiper>
					<div className="hero-content-layer">
						<div className="container-main">
							<motion.div 
								className="home-page-content"
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8 }}
							>
								<h1 className="home-main-heading montserrat">
									Branmont Global Secondary School
									for Focused,
									<span className="hero-line-block montserrat">Purpose-Driven Learners</span>
								</h1>
								<p className="home-sub-text">Branmont Global Secondary School offers integrated +1 &amp; +2 education with coaching for NEET, JEE, CA, CMA, and competitive exams&mdash;along with professional pathways and values-based campus learning.</p>
								<div className="hero-btns">
									<a className="btn enquire-btn" href="https://admission.branmont.com/" target="_blank" rel="noopener noreferrer">Apply Now</a>
								</div>
							</motion.div>
						</div>
					</div>
				</div>

				<motion.section
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true, margin: "-100px" }}
					transition={{ duration: 0.8 }}
				>
					<About />
				</motion.section>

				<motion.section
					initial={{ opacity: 0, y: 50 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Path />
				</motion.section>

				<motion.section
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Programes />
				</motion.section>

				<motion.section
					initial={{ opacity: 0, scale: 0.95 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Blog />
				</motion.section>

				<motion.div 
					className="student-dev-section"
					initial={{ opacity: 0, y: 50 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<div className="container-main">
						<div className="row">
							<div className="col-lg-12 text-center mb-5">
								<h5 className="section-small-label montserrat">Holistic Growth</h5>
								<h2 className="section-main-title montserrat">Student Development <span className="highlight-text">Focus</span></h2>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-user-star-line"></i></div>
									<h5>Leadership &amp; Confidence</h5>
									<p>Building strong, confident future leaders.</p>
								</div>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-mic-2-line"></i></div>
									<h5>Public Speaking</h5>
									<p>Communication skills for real-world success.</p>
								</div>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-road-map-line"></i></div>
									<h5>Career Clarity</h5>
									<p>Mentorship to define your career path early.</p>
								</div>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-heart-pulse-line"></i></div>
									<h5>Emotional Well-being</h5>
									<p>A supportive environment for mental wellness.</p>
								</div>
							</div>
						</div>
					</div>
				</motion.div>

				<motion.div 
					className="faq-section-main"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 1 }}
				>
					<div className="container-main">
						<div className="row align-items-center">
							<div className="col-lg-5 mb-5 mb-lg-0">
								<div className="faq-left-panel">
									<h5 className="section-small-label">Got Questions?</h5>
									<h2 className="faq-main-title montserrat">Frequently<br />Asked Questions</h2>
									<p className="faq-left-desc">Find answers to the most common questions about admissions, programs, and life at Branmont Global Secondary School.</p>
									<Link to="/contact" className="btn faq-contact-btn">Contact Us <i className="ri-arrow-right-line"></i></Link>
								</div>
							</div>
							<div className="col-lg-7">
								<div className="faq-items-wrap">
									{faqs.map((faq, idx) => (
										<motion.div
											className={`faq-card${openFaq === idx ? " faq-open" : ""}`}
											key={idx}
											onClick={() => toggleFaq(idx)}
											initial={{ opacity: 0, x: 20 }}
											whileInView={{ opacity: 1, x: 0 }}
											transition={{ delay: idx * 0.1 }}
											viewport={{ once: true }}
										>
											<div className="faq-card-header">
												<span className="faq-number">{String(idx + 1).padStart(2, "0")}</span>
												<h5 className="faq-question">{faq.q}</h5>
												<span className="faq-toggle-icon">
													<i className={openFaq === idx ? "ri-subtract-line" : "ri-add-line"}></i>
												</span>
											</div>
											{openFaq === idx && (
												<div className="faq-answer">
													<p>{faq.a}</p>
												</div>
											)}
										</motion.div>
									))}
								</div>
							</div>
						</div>
					</div>
				</motion.div>
			</div>
			<Footer />
		</div>
	)
}

export default HomePage
